import React, { useState } from 'react';
import {
    Box,
    LinearProgress,
    Typography,
    Chip,
    Alert,
    AlertTitle,
    Collapse,
    IconButton,
    Paper,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MessageIcon from '@mui/icons-material/Message';
import { PortfolioLpoa } from '../../clients/graphql/pages/lpoa-queries';
import { getLpoaStatus } from '../../utils/lpoaStatus';
import { formatMoment } from '../../functions/momentFormatter';

export interface LpoaLoaderProps {
    lpoaData?: PortfolioLpoa[];
    portfolioKey?: number;
}

export type LpoaState = 
    | 'document-created'
    | 'signed'
    | 'active'
    | 'rejected'
    | 'revoked'
    | 'empty'
    | 'none';

export const LpoaLoader: React.FC<LpoaLoaderProps> = ({ lpoaData, portfolioKey }) => {
    const [expanded, setExpanded] = useState(false);
    
    // Get the most recent LPOA record for this portfolio
    const portfolioLpoaData = portfolioKey 
        ? lpoaData?.filter(lpoa => lpoa.portfolioKey === portfolioKey) || []
        : lpoaData || [];
    
    // Sort by creation date (most recent first)
    const sortedLpoa = [...portfolioLpoaData].sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    const latestLpoa = sortedLpoa[0];
    const lpoaStatus = latestLpoa ? getLpoaStatus(latestLpoa) : null;

    // Determine the current state
    const getLpoaState = (): LpoaState => {
        if (!latestLpoa) return 'none';
        if (latestLpoa.revokedAt) return 'revoked';
        if (latestLpoa.rejectedAt) return 'rejected';
        if (lpoaStatus?.isActive) return 'active';
        if (lpoaStatus?.isSigned) return 'signed';
        if (latestLpoa.envelopeId && !latestLpoa.signedAt) return 'document-created';
        if (latestLpoa.createdAt && !latestLpoa.envelopeId) return 'empty';
        return 'none';
    };

    const currentState = getLpoaState();

    // Set default expanded state: collapsed for active LPOA, expanded for others
    React.useEffect(() => {
        setExpanded(currentState !== 'active');
    }, [currentState]);

    if (!lpoaData || lpoaData.length === 0 || currentState === 'none') {
        return null;
    }

    // Get progress percentage
    const getProgressPercentage = (): number => {
        switch (currentState) {
            case 'empty': return 0;
            case 'document-created': return 20;
            case 'signed': return 50;
            case 'active': return 100;
            case 'rejected': return 0;
            case 'revoked': return 0;
            default: return 0;
        }
    };

    // Get state color
    const getStateColor = (): string => {
        switch (currentState) {
            case 'empty': return '#e0e0e0'; // Light grey
            case 'document-created': return '#ff9800'; // Orange
            case 'signed': return '#2196f3'; // Blue
            case 'active': return '#4caf50'; // Green
            case 'rejected': return '#f44336'; // Red
            case 'revoked': return '#9e9e9e'; // Grey
            default: return '#e0e0e0';
        }
    };

    // Get state label
    const getStateLabel = (): string => {
        switch (currentState) {
            case 'empty': return 'Empty';
            case 'document-created': return 'Document Created';
            case 'signed': return 'Signed';
            case 'active': return 'Active';
            case 'rejected': return 'Rejected';
            case 'revoked': return 'Revoked';
            default: return 'Unknown';
        }
    };

    // Get status message
    const getStatusMessage = (): string => {
        switch (currentState) {
            case 'empty':
                return `LPOA record created on ${formatMoment(latestLpoa.createdAt.toLocaleString())} - No DocuSign envelope ID assigned yet`;
            case 'document-created':
                return `Document created on ${formatMoment(latestLpoa.createdAt.toLocaleString())} - Awaiting signature`;
            case 'signed':
                return `Signed on ${formatMoment(latestLpoa.signedAt!.toLocaleString())} - Awaiting activation`;
            case 'active':
                return `Activated on ${formatMoment(latestLpoa.activatedAt!.toLocaleString())} - Portfolio is managed under LPOA`;
            case 'rejected':
                return `Rejected on ${formatMoment(latestLpoa.rejectedAt!.toLocaleString())} - Schwab rejected the signature`;
            case 'revoked':
                return `Revoked on ${formatMoment(latestLpoa.revokedAt!.toLocaleString())} - User removed LPOA access`;
            default:
                return '';
        }
    };

    const progressPercentage = getProgressPercentage();
    const stateColor = getStateColor();
    const stateLabel = getStateLabel();
    const statusMessage = getStatusMessage();

    return (
        <Box sx={{ mb: 2 }}>
            <Alert 
                severity={currentState === 'active' ? 'success' : currentState === 'rejected' || currentState === 'revoked' ? 'error' : currentState === 'empty' ? 'warning' : 'info'}
                sx={{
                    '& .MuiAlert-message': {
                        width: '100%',
                    }
                }}
            >
                <AlertTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expanded ? 1 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" component="span">
                            LPOA Status
                        </Typography>
                        <Chip 
                            label={stateLabel}
                            size="small"
                            sx={{
                                backgroundColor: stateColor,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                            }}
                        />
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{ ml: 1 }}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </AlertTitle>
                
                <Collapse in={expanded}>
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Progress
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {progressPercentage}%
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={progressPercentage}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: stateColor,
                                    borderRadius: 3,
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                Created
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                Signed
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                Active
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {statusMessage}
                    </Typography>
                    
                    {latestLpoa.envelopeId && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '0.75rem' }}>
                            Envelope ID: {latestLpoa.envelopeId}
                        </Typography>
                    )}
                    
                    {currentState === 'rejected' && latestLpoa.rejectedReason && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <MessageIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                    Revocation Reason
                                </Typography>
                            </Box>
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 1.5, 
                                    backgroundColor: 'rgba(158, 158, 158, 0.05)',
                                    border: '1px solid rgba(158, 158, 158, 0.2)',
                                    borderRadius: 1
                                }}
                            >
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        fontSize: '0.8rem',
                                        lineHeight: 1.5,
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {latestLpoa.rejectedReason}
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                </Collapse>
            </Alert>
        </Box>
    );
};